import React from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { toUpperCaseNoAccent } from 'utils/stringHelper';

import {
    fetchAdministrationChargerContactExclus,
    postChargerContactExclu,
    deleteChargerContactExclu
} from 'utils/ts-queries';
import { ApiChargerContactExclu, ApiChargerContactExcluNiveau } from 'types/apiReturns';

const QUERY_KEY = 'administrationChargerContactExclus';

const AdministrationChargerContactExclus: React.FC = () => {

    const queryClient = useQueryClient();

    const { isLoading, isError, data } = useQuery({
        queryKey: [QUERY_KEY],
        queryFn: fetchAdministrationChargerContactExclus
    });

    const [addDialogOpen, setAddDialogOpen] = React.useState<boolean>(false);
    const [excluToDelete, setExcluToDelete] = React.useState<ApiChargerContactExclu | null>(null);

    const invalidate = () => queryClient.invalidateQueries([QUERY_KEY]);

    const postMutation = useMutation({
        mutationFn: postChargerContactExclu,
        onSuccess: () => { invalidate(); setAddDialogOpen(false); }
    });

    const deleteMutation = useMutation({
        mutationFn: deleteChargerContactExclu,
        onSuccess: () => { invalidate(); setExcluToDelete(null); }
    });

    if (isLoading) {
        return <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>;
    }

    if (isError || !data) {
        return <Typography>Aucune donnée disponible.</Typography>;
    }

    const { items, niveaux } = data;

    const libelleNiveau = (niveau: number) => niveaux.find(n => n.id === niveau)?.libelle ?? niveau;

    return (
        <Card sx={{ p: 2, mb: 3 }}>
            {addDialogOpen && (
                <AddChargerContactExcluDialog
                    niveaux={niveaux}
                    isSaving={postMutation.isLoading}
                    onClose={() => setAddDialogOpen(false)}
                    onSave={(niveau, code) => postMutation.mutate({ niveau, code })}
                />
            )}

            {excluToDelete && (
                <DeleteChargerContactExcluDialog
                    exclu={excluToDelete}
                    libelleNiveau={libelleNiveau(excluToDelete.niveau)}
                    isDeleting={deleteMutation.isLoading}
                    onClose={() => setExcluToDelete(null)}
                    onDelete={() => deleteMutation.mutate(excluToDelete.id)}
                />
            )}

            <Box sx={{ display: 'flex', width: '100%' }}>
                <Typography gutterBottom variant="h5" sx={{ flex: '1 1 auto' }}>
                    Administration des exclusions du chargement des contacts
                </Typography>
                <Button sx={{ flex: '0 1 auto' }} onClick={() => setAddDialogOpen(true)}>Nouveau</Button>
            </Box>

            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Niveau</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map(exclu => (
                            <TableRow key={exclu.id} hover>
                                <TableCell>{libelleNiveau(exclu.niveau)}</TableCell>
                                <TableCell>{exclu.code}</TableCell>
                                <TableCell>
                                    <Button onClick={() => setExcluToDelete(exclu)}>Supprimer</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Card>
    );
};

type AddDialogProps = {
    niveaux: ApiChargerContactExcluNiveau[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (niveau: number, code: string) => void;
};

const AddChargerContactExcluDialog: React.FC<AddDialogProps> = ({ niveaux, isSaving, onClose, onSave }) => {

    const [niveau, setNiveau] = React.useState<number>(niveaux[0]?.id ?? 0);
    const [code, setCode] = React.useState<string>('');

    return (
        <Dialog open>
            <DialogTitle>Nouvelle exclusion du chargement des contacts</DialogTitle>

            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <TextField
                        select
                        label="Niveau"
                        required
                        value={niveau}
                        onChange={e => setNiveau(Number(e.target.value))}
                    >
                        {niveaux.map(n => <MenuItem key={n.id} value={n.id}>{n.libelle}</MenuItem>)}
                    </TextField>

                    <TextField
                        label="Code"
                        required
                        fullWidth
                        placeholder="Saisir un code"
                        value={code}
                        onChange={e => setCode(toUpperCaseNoAccent(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">Annuler</Button>
                <Button
                    onClick={() => onSave(niveau, code)}
                    variant="contained"
                    color="secondary"
                    disabled={!code || isSaving}
                >
                    {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

type DeleteDialogProps = {
    exclu: ApiChargerContactExclu;
    libelleNiveau: string | number;
    isDeleting: boolean;
    onClose: () => void;
    onDelete: () => void;
};

const DeleteChargerContactExcluDialog: React.FC<DeleteDialogProps> = ({ exclu, libelleNiveau, isDeleting, onClose, onDelete }) => {
    return (
        <Dialog open>
            <DialogTitle>Suppression d'une exclusion du chargement des contacts</DialogTitle>

            <DialogContent>
                <p>Êtes-vous sûr de vouloir supprimer l'exclusion pour le niveau <strong>{libelleNiveau}</strong> avec le code <strong>{exclu.code}</strong> ?</p>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">Annuler</Button>
                <Button
                    onClick={onDelete}
                    variant="contained"
                    color="secondary"
                    disabled={isDeleting}
                >
                    {isDeleting ? 'Suppression…' : 'Supprimer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdministrationChargerContactExclus;
